package com.example.positionahead.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Checkbox
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.example.positionahead.model.AccountColors
import com.example.positionahead.model.SavedPortfolio
import com.example.positionahead.ui.theme.colorFromHex

@Composable
fun AccountSwitcher(
    accounts: List<SavedPortfolio>,
    activeAccountId: String,
    onSelectAccount: (String) -> Unit,
    onCreateAccount: (name: String, copyFromId: String?, colorHex: String, startDate: String?, notes: String) -> Unit,
    onUpdateAccount: (id: String, name: String, colorHex: String, startDate: String?, notes: String) -> Unit,
    onDeleteAccount: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val active = accounts.find { it.id == activeAccountId } ?: accounts.firstOrNull()
    var menuExpanded by remember { mutableStateOf(false) }
    var manageOpen by remember { mutableStateOf(false) }

    Box {
        Row(modifier = modifier.clickable { menuExpanded = true }) {
            active?.let { account ->
                Box(
                    modifier = Modifier
                        .size(12.dp)
                        .clip(CircleShape)
                        .background(colorFromHex(account.colorHex))
                        .align(Alignment.CenterVertically),
                )
            }
            Column(modifier = Modifier.padding(start = 8.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = active?.name ?: "Account",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                    )
                    Text(" ▼", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
                }
                active?.startDate?.let { date ->
                    Text(
                        text = "Since $date",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                    )
                }
            }
        }
        DropdownMenu(expanded = menuExpanded, onDismissRequest = { menuExpanded = false }) {
            accounts.forEach { account ->
                DropdownMenuItem(
                    text = {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Box(
                                modifier = Modifier
                                    .size(10.dp)
                                    .clip(CircleShape)
                                    .background(colorFromHex(account.colorHex)),
                            )
                            Text(
                                text = account.name + if (account.id == activeAccountId) " ✓" else "",
                                modifier = Modifier.padding(start = 8.dp),
                            )
                        }
                    },
                    onClick = {
                        onSelectAccount(account.id)
                        menuExpanded = false
                    },
                )
            }
            HorizontalDivider()
            DropdownMenuItem(
                text = { Text("Manage accounts…") },
                onClick = {
                    menuExpanded = false
                    manageOpen = true
                },
            )
        }
    }

    if (manageOpen) {
        ManageAccountsDialog(
            accounts = accounts,
            activeAccountId = activeAccountId,
            onDismiss = { manageOpen = false },
            onSelectAccount = {
                onSelectAccount(it)
                manageOpen = false
            },
            onCreateAccount = onCreateAccount,
            onUpdateAccount = onUpdateAccount,
            onDeleteAccount = onDeleteAccount,
        )
    }
}

@Composable
private fun ManageAccountsDialog(
    accounts: List<SavedPortfolio>,
    activeAccountId: String,
    onDismiss: () -> Unit,
    onSelectAccount: (String) -> Unit,
    onCreateAccount: (name: String, copyFromId: String?, colorHex: String, startDate: String?, notes: String) -> Unit,
    onUpdateAccount: (id: String, name: String, colorHex: String, startDate: String?, notes: String) -> Unit,
    onDeleteAccount: (String) -> Unit,
) {
    var editingId by remember { mutableStateOf<String?>(null) }
    var creating by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Accounts") },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                accounts.forEach { account ->
                    if (editingId == account.id) {
                        AccountForm(
                            title = "Edit account",
                            initialName = account.name,
                            initialColor = account.colorHex,
                            initialStartDate = account.startDate.orEmpty(),
                            initialNotes = account.notes,
                            showCopyFrom = false,
                            copyFromOptions = emptyList(),
                            onSave = { name, color, startDate, notes, _ ->
                                onUpdateAccount(account.id, name, color, startDate, notes)
                                editingId = null
                            },
                            onCancel = { editingId = null },
                        )
                    } else {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { onSelectAccount(account.id) }
                                .padding(vertical = 4.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Box(
                                    modifier = Modifier
                                        .size(14.dp)
                                        .clip(CircleShape)
                                        .background(colorFromHex(account.colorHex)),
                                )
                                Column(modifier = Modifier.padding(start = 8.dp)) {
                                    Text(
                                        account.name + if (account.id == activeAccountId) " (active)" else "",
                                        fontWeight = FontWeight.Medium,
                                    )
                                    Text(
                                        "${account.positions.size} positions",
                                        style = MaterialTheme.typography.labelSmall,
                                    )
                                }
                            }
                            Row {
                                TextButton(onClick = { editingId = account.id }) { Text("Edit") }
                                if (accounts.size > 1) {
                                    TextButton(onClick = { onDeleteAccount(account.id) }) { Text("Delete") }
                                }
                            }
                        }
                    }
                }

                if (creating) {
                    AccountForm(
                        title = "New account",
                        initialName = "",
                        initialColor = AccountColors.presets[accounts.size % AccountColors.presets.size],
                        initialStartDate = "",
                        initialNotes = "",
                        showCopyFrom = accounts.isNotEmpty(),
                        copyFromOptions = accounts,
                        onSave = { name, color, startDate, notes, copyFromId ->
                            onCreateAccount(name, copyFromId, color, startDate, notes)
                            creating = false
                        },
                        onCancel = { creating = false },
                    )
                } else {
                    TextButton(onClick = { creating = true }) {
                        Text("+ New account")
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("Done") }
        },
    )
}

@Composable
private fun AccountForm(
    title: String,
    initialName: String,
    initialColor: String,
    initialStartDate: String,
    initialNotes: String,
    showCopyFrom: Boolean,
    copyFromOptions: List<SavedPortfolio>,
    onSave: (name: String, colorHex: String, startDate: String?, notes: String, copyFromId: String?) -> Unit,
    onCancel: () -> Unit,
) {
    var name by remember(initialName) { mutableStateOf(initialName) }
    var color by remember(initialColor) { mutableStateOf(initialColor) }
    var startDate by remember(initialStartDate) { mutableStateOf(initialStartDate) }
    var notes by remember(initialNotes) { mutableStateOf(initialNotes) }
    var copyFrom by remember { mutableStateOf(true) }
    var copySourceId by remember { mutableStateOf(copyFromOptions.firstOrNull()?.id) }

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(title, style = MaterialTheme.typography.labelLarge)
        OutlinedTextField(
            value = name,
            onValueChange = { name = it },
            label = { Text("Account name") },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )
        Text("Color", style = MaterialTheme.typography.labelMedium)
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            AccountColors.presets.forEach { preset ->
                Box(
                    modifier = Modifier
                        .size(28.dp)
                        .clip(CircleShape)
                        .background(colorFromHex(preset))
                        .border(
                            width = if (color == preset) 2.dp else 0.dp,
                            color = MaterialTheme.colorScheme.primary,
                            shape = CircleShape,
                        )
                        .clickable { color = preset },
                )
            }
        }
        OutlinedTextField(
            value = startDate,
            onValueChange = { startDate = it },
            label = { Text("Start date (when positions began)") },
            placeholder = { Text("8/1/2026") },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )
        OutlinedTextField(
            value = notes,
            onValueChange = { notes = it },
            label = { Text("Notes (optional)") },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )
        if (showCopyFrom && copyFromOptions.isNotEmpty()) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(checked = copyFrom, onCheckedChange = { copyFrom = it })
                Text("Copy positions from existing account")
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            TextButton(onClick = {
                if (name.isNotBlank()) {
                    onSave(
                        name.trim(),
                        color,
                        startDate.takeIf { it.isNotBlank() },
                        notes.trim(),
                        if (copyFrom) copySourceId else null,
                    )
                }
            }) { Text("Save") }
            TextButton(onClick = onCancel) { Text("Cancel") }
        }
    }
}
